import {
  EnvironmentId,
  LocalHostSessionCredential,
  SshEnvironmentTarget,
} from "@afar-research/contracts";
import { isLoopbackHostname } from "@afar-research/shared/loopback";
import type * as Effect from "effect/Effect";
import * as Schema from "effect/Schema";

const ConnectionTargetBase = {
  environmentId: EnvironmentId,
  label: Schema.String,
};

export class PrimaryConnectionTarget extends Schema.TaggedClass<PrimaryConnectionTarget>()(
  "PrimaryConnectionTarget",
  {
    ...ConnectionTargetBase,
    httpBaseUrl: Schema.String,
    wsBaseUrl: Schema.String,
  },
) {}

export class RemoteConnectionTarget extends Schema.TaggedClass<RemoteConnectionTarget>()(
  "RemoteConnectionTarget",
  {
    ...ConnectionTargetBase,
    /**
     * Last known, and a placeholder the resolver replaces: a Cloud Workspace answers at whatever
     * hostname its hosting has, which AFAR Connect names when it brokers the introduction rather
     * than this client's record of it (ADR-0132).
     */
    httpBaseUrl: Schema.String,
    wsBaseUrl: Schema.String,
  },
) {}

/**
 * An Environment on a machine the user reaches with their own SSH.
 *
 * No base URLs are stored, unlike every other target: an SSH Environment answers on a loopback
 * port that exists only while this client holds a forward open, so the only durable fact is the
 * destination. What it answers at is decided when the forward is opened.
 */
export class SshConnectionTarget extends Schema.TaggedClass<SshConnectionTarget>()(
  "SshConnectionTarget",
  {
    ...ConnectionTargetBase,
    ssh: SshEnvironmentTarget,
  },
) {}

/**
 * An Environment this device paired with by link, reached directly at the address the link named.
 *
 * The session is the Environment Access that Environment issued when the link's grant was
 * redeemed; every connection presents it, and a revoked one fails as `revoked` rather than retrying.
 */
export class PairedConnectionTarget extends Schema.TaggedClass<PairedConnectionTarget>()(
  "PairedConnectionTarget",
  {
    ...ConnectionTargetBase,
    httpBaseUrl: Schema.String,
    wsBaseUrl: Schema.String,
    session: Schema.String,
  },
) {}

export const ConnectionTarget = Schema.Union([
  PrimaryConnectionTarget,
  RemoteConnectionTarget,
  SshConnectionTarget,
  PairedConnectionTarget,
]);
export type ConnectionTarget =
  | PrimaryConnectionTarget
  | RemoteConnectionTarget
  | SshConnectionTarget
  | PairedConnectionTarget;

export type ConnectionTargetKind = ConnectionTarget["_tag"];

/**
 * Whether this client runs on the same machine as the Environment behind `target`, so a path
 * picked on the client's own disk names the same file on the Environment host.
 *
 * Only the primary Environment reached on loopback qualifies: the Desktop Client's own backend,
 * or the Host Web Client opened on the host. A browser that reached the primary over the network,
 * and every saved, paired, or SSH Environment, is another machine — an SSH forward answers on
 * loopback here while the files live there.
 */
export function connectionTargetSharesClientMachine(target: ConnectionTarget): boolean {
  if (target._tag !== "PrimaryConnectionTarget") return false;
  try {
    return isLoopbackHostname(new URL(target.httpBaseUrl).hostname);
  } catch {
    return false;
  }
}

export type NetworkStatus = "unknown" | "offline" | "online";

export const ConnectionTransientReason = Schema.Literals([
  "network",
  "timeout",
  "transport",
  "endpoint-unavailable",
  /** The Environment exists but its host is still starting, such as a Cloud Workspace run. */
  "environment-starting",
  "host-unavailable",
  /** The SSH forward could not be opened or did not survive. Reachability, not authorization. */
  "ssh-forward",
]);
export type ConnectionTransientReason = typeof ConnectionTransientReason.Type;

export const ConnectionBlockedReason = Schema.Literals([
  "configuration",
  "credential",
  "revoked",
  /** The SSH host key did not verify. Never retried: a changed key is answered by the user. */
  "ssh-host-key",
  /** The destination refused the SSH credential. Answered by the user's SSH setup, not by a
   * sign-in: reporting it as an account credential sends them to the wrong layer. */
  "ssh-credential",
  "unsupported",
]);
export type ConnectionBlockedReason = typeof ConnectionBlockedReason.Type;

export class ConnectionTransientError extends Schema.TaggedErrorClass<ConnectionTransientError>()(
  "ConnectionTransientError",
  {
    reason: ConnectionTransientReason,
    detail: Schema.String,
    traceId: Schema.optionalKey(Schema.String),
  },
) {
  override get message(): string {
    return this.detail;
  }
}

export class ConnectionBlockedError extends Schema.TaggedErrorClass<ConnectionBlockedError>()(
  "ConnectionBlockedError",
  {
    reason: ConnectionBlockedReason,
    detail: Schema.String,
    traceId: Schema.optionalKey(Schema.String),
  },
) {
  override get message(): string {
    return this.detail;
  }
}

export type ConnectionAttemptError = ConnectionTransientError | ConnectionBlockedError;

export interface PreparedConnection {
  readonly environmentId: EnvironmentId;
  readonly label: string;
  readonly httpBaseUrl: string;
  readonly socketUrl: string;
  readonly socketProtocols?: string;
  readonly target: ConnectionTarget;
  /**
   * An authorized URL for another WebSocket on the same Environment, such as the
   * Collaborative Browser's view (ADR-0137): a fresh one-use ticket from the same
   * credential the connection used, or nothing where a browser's cookie rides
   * the upgrade on its own. Absent on connections that cannot open another.
   */
  readonly authorizeSocket?: (path: string) => Effect.Effect<string, ConnectionAttemptError>;
}

export type SupervisorConnectionPhase =
  | "available"
  | "offline"
  | "connecting"
  | "backoff"
  | "connected"
  | "blocked";

export type ConnectionAttemptStage = "preparing" | "opening" | "synchronizing";

export interface SupervisorConnectionState {
  readonly desired: boolean;
  readonly network: NetworkStatus;
  readonly phase: SupervisorConnectionPhase;
  readonly stage: ConnectionAttemptStage | null;
  readonly attempt: number;
  readonly generation: number;
  readonly lastFailure: ConnectionAttemptError | null;
  readonly retryAt: number | null;
}

export type ClientReadiness = "unavailable" | "starting" | "synchronizing" | "ready";

export function clientReadiness(state: SupervisorConnectionState): ClientReadiness {
  switch (state.phase) {
    case "connecting":
      return state.stage === "synchronizing" ? "synchronizing" : "starting";
    case "connected":
      return "ready";
    case "available":
    case "offline":
    case "backoff":
    case "blocked":
      return "unavailable";
  }
}

export const AVAILABLE_CONNECTION_STATE: SupervisorConnectionState = Object.freeze({
  desired: false,
  network: "unknown",
  phase: "available",
  stage: null,
  attempt: 0,
  generation: 0,
  lastFailure: null,
  retryAt: null,
});
